export type WorkAttentionLevel =
  | 'critical'
  | 'attention'
  | 'active'
  | 'complete'
  | 'closed';

export type WorkStageState =
  | 'pending'
  | 'running'
  | 'success'
  | 'failed'
  | 'blocked'
  | 'unknown'
  | 'skipped';

export type WorkRadarView =
  | 'human'
  | 'failed'
  | 'unlinked'
  | 'stale'
  | 'active'
  | 'complete'
  | 'all';

export type WorkInboxView = 'mine' | 'team' | 'approvals' | 'unowned';

export type WorkInboxCounts = Record<WorkInboxView, number>;

export function defaultWorkInboxView(counts: WorkInboxCounts): WorkInboxView {
  if (counts.mine > 0) return 'mine';
  if (counts.team > 0) return 'team';
  if (counts.approvals > 0) return 'approvals';
  if (counts.unowned > 0) return 'unowned';
  return 'mine';
}

type WorkRadarFilterItem = {
  attentionLevel: WorkAttentionLevel;
  hasFailedSignal: boolean;
  isStale: boolean;
  waitingOnHuman: boolean;
  hasLinkGap: boolean;
};

// Shared ordering keeps urgent journeys at the top across radar surfaces.
export function workAttentionRank(level: WorkAttentionLevel) {
  return {
    critical: 0,
    attention: 1,
    active: 2,
    complete: 3,
    closed: 4,
  }[level];
}

export function matchesWorkRadarView(
  view: WorkRadarView,
  item: WorkRadarFilterItem
) {
  if (view === 'human') return item.waitingOnHuman;
  if (view === 'failed') return item.hasFailedSignal;
  if (view === 'unlinked') return item.hasLinkGap;
  if (view === 'stale') return item.isStale;
  if (view === 'active') return item.attentionLevel === 'active';
  if (view === 'complete') {
    return ['complete', 'closed'].includes(item.attentionLevel);
  }
  return true;
}

export function workRadarStatusLabel(item: WorkRadarFilterItem) {
  if (item.waitingOnHuman) return 'Human action';
  if (item.hasFailedSignal) return 'Failed';
  if (item.attentionLevel === 'critical') return 'Blocked';
  if (item.isStale) return 'Stale';
  return {
    attention: 'Needs attention',
    active: 'Progressing',
    complete: 'Delivered',
    closed: 'Closed',
    critical: 'Blocked',
  }[item.attentionLevel];
}

export function matchesWorkRadarStage(
  selectedStage: string,
  stages: Array<{ key: string; state: WorkStageState }>
) {
  if (!selectedStage) return true;
  const stage = stages.find((candidate) => candidate.key === selectedStage);
  return Boolean(stage && !['unknown', 'skipped'].includes(stage.state));
}
